import React, { useEffect, useState } from "react";
import Modal from "../components/Modal";
import {
  createUserMood,
  getUserMood,
  listUserMoods,
  updateUserMood,
} from "../api";
import ScenarioPromptEditor from "../components/ScenarioPromptEditor";

const BTN_GHOST =
  "inline-flex items-center justify-center rounded-xl border border-transparent bg-transparent px-3 py-2 text-sm font-semibold hover:bg-[#0e1320] disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_PRIMARY =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-violet-600/40 bg-violet-600/20 px-4 py-2 text-sm font-semibold hover:bg-violet-600/30 disabled:opacity-60 disabled:cursor-not-allowed";
const INP =
  "w-full rounded-xl border border-[#1f2638] bg-[#0f1523] px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-violet-600/40 focus:border-violet-500/40";

const EMPTY_FORM = {
  name: "",
  description: "",
  prompt_fragment: "",
};

function dash(v) {
  return v == null || String(v).trim() === "" ? "—" : String(v);
}

function pickDocId(x) {
  return String(x?.doc_id ?? x?.id ?? x?._id ?? "").trim();
}

export default function UserMoods() {
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listErr, setListErr] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [toast, setToast] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create");
  const [editorDocId, setEditorDocId] = useState("");
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorErr, setEditorErr] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setListErr("");
        setLoadingList(true);
        const arr = await listUserMoods();
        if (!alive) return;
        setItems(Array.isArray(arr) ? arr : []);
      } catch (e) {
        if (!alive) return;
        setItems([]);
        setListErr(e?.message || "Грешка при зареждане на потребителските настроения.");
      } finally {
        if (alive) setLoadingList(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  useEffect(() => {
    if (!editorOpen || editorMode !== "edit" || !editorDocId) return;
    let alive = true;

    const run = async () => {
      try {
        setEditorErr("");
        setEditorLoading(true);
        const data = await getUserMood(editorDocId);
        if (!alive) return;
        setForm({
          name: String(data?.name ?? ""),
          description: String(data?.description ?? ""),
          prompt_fragment: String(data?.prompt_fragment ?? ""),
        });
      } catch (e) {
        if (!alive) return;
        setEditorErr(e?.message || "Грешка при зареждане на детайли.");
      } finally {
        if (alive) setEditorLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [editorDocId, editorMode, editorOpen]);

  const openCreate = () => {
    setEditorMode("create");
    setEditorDocId("");
    setForm({ ...EMPTY_FORM });
    setEditorErr("");
    setEditorLoading(false);
    setEditorSaving(false);
    setEditorOpen(true);
  };

  const openEdit = (row) => {
    const id = pickDocId(row);
    if (!id) return;
    setEditorMode("edit");
    setEditorDocId(id);
    setForm({ ...EMPTY_FORM });
    setEditorErr("");
    setEditorLoading(true);
    setEditorSaving(false);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (editorSaving) return;
    setEditorOpen(false);
    setEditorErr("");
    setEditorLoading(false);
  };

  const validate = () => {
    if (!String(form?.name || "").trim()) return "Името е задължително.";
    if (!String(form?.description || "").trim()) return "Описанието е задължително.";
    if (!String(form?.prompt_fragment || "").trim()) return "Prompt fragment е задължителен.";
    return "";
  };

  const saveEditor = async () => {
    const validationErr = validate();
    if (validationErr) {
      setEditorErr(validationErr);
      return;
    }

    const payload = {
      name: String(form?.name || "").trim(),
      description: String(form?.description || "").trim(),
      prompt_fragment: String(form?.prompt_fragment || ""),
      active: true,
    };

    try {
      setEditorErr("");
      setEditorSaving(true);

      if (editorMode === "edit") {
        await updateUserMood(editorDocId, payload);
        setToast("Настроението е обновено.");
      } else {
        await createUserMood(payload);
        setToast("Настроението е създадено.");
      }

      setEditorOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setEditorErr(e?.message || "Грешка при запис.");
    } finally {
      setEditorSaving(false);
    }
  };

  return (
    <>
      <div className="card rounded-2xl p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-bold whitespace-nowrap">Потребителски настроения</h2>
          <div className="ml-auto" />
          <button className={BTN_PRIMARY} onClick={openCreate}>
            Добави настроение
          </button>
        </div>

        {!!listErr && (
          <div className="mt-4 rounded-xl border border-rose-600/30 bg-rose-600/10 px-4 py-3 text-sm text-rose-200">
            {listErr}
          </div>
        )}

        <div className="mt-4 space-y-3">
          {loadingList ? (
            <div className="text-sm text-slate-300">Зареждане...</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-slate-300">Няма добавени потребителски настроения.</div>
          ) : (
            items.map((row, idx) => {
              const id = pickDocId(row);
              const isActive = row?.active === true;
              return (
                <button
                  key={id || `${row?.name || "row"}-${idx}`}
                  type="button"
                  onClick={() => openEdit(row)}
                  className="w-full rounded-2xl border border-[#1f2638] bg-[#0f1523] px-4 py-3 text-left transition hover:bg-[#121825]"
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-200 truncate">{dash(row?.name)}</div>
                      <div className="text-sm text-slate-400 truncate mt-0.5">
                        {dash(row?.description)}
                      </div>
                    </div>
                    <span
                      className={[
                        "shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium",
                        isActive
                          ? "border-emerald-600/40 bg-emerald-600/15 text-emerald-200"
                          : "border-slate-700 bg-slate-700/30 text-slate-300",
                      ].join(" ")}
                    >
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      <Modal
        open={editorOpen}
        title={editorMode === "edit" ? "Редакция на потребителско настроение" : "Добавяне на потребителско настроение"}
        onClose={closeEditor}
        scroll={false}
        panelClassName="max-w-4xl w-[94vw] md:w-[980px]"
        footer={
          <>
            <button className={BTN_GHOST} onClick={closeEditor} disabled={editorSaving}>
              Cancel
            </button>
            <button className={BTN_PRIMARY} onClick={saveEditor} disabled={editorSaving || editorLoading}>
              {editorSaving ? "Запис..." : "Запази"}
            </button>
          </>
        }
      >
        <div
          className="max-h-[70vh] overflow-auto pr-4 nice-scrollbar"
          style={{ scrollbarGutter: "stable both-edges" }}
        >
          {editorLoading ? (
            <div className="text-sm text-slate-300">Зареждане...</div>
          ) : (
            <div className="space-y-4">
              {!!editorErr && (
                <div className="rounded-xl border border-rose-600/30 bg-rose-600/10 px-4 py-3 text-sm text-rose-200">
                  {editorErr}
                </div>
              )}

              <div>
                <div className="text-xs text-slate-400 mb-1">Име</div>
                <input
                  className={INP}
                  value={form?.name || ""}
                  onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Име на настроението"
                  disabled={editorSaving}
                />
              </div>

              <div>
                <div className="text-xs text-slate-400 mb-1">Описание</div>
                <textarea
                  className={[INP, "min-h-[96px] resize-y"].join(" ")}
                  value={form?.description || ""}
                  onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                  placeholder="Описание"
                  disabled={editorSaving}
                  style={{ scrollbarGutter: "stable" }}
                />
              </div>

              <div>
                <div className="text-xs text-slate-400 mb-1">Описание на настроението</div>
                <ScenarioPromptEditor
                  value={form?.prompt_fragment || ""}
                  onChange={(v) => setForm((s) => ({ ...s, prompt_fragment: v }))}
                  placeholder=""
                  showVariableChips={false}
                  highlightVariables={false}
                  disabled={editorSaving}
                  editorClassName="h-[260px] max-h-[45vh]"
                />
              </div>
            </div>
          )}
        </div>
      </Modal>

      {!!toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[120]">
          <div className="rounded-xl border border-slate-800/60 bg-[#0f1523] px-5 py-3 shadow-lg text-sm text-slate-200">
            {toast}
          </div>
        </div>
      )}
    </>
  );
}
